import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    HardDrive, Database, ArrowRight, Camera, Loader2, Play,
    AlertTriangle, CheckCheck, RefreshCw, Pause, X, Zap, Activity,
    FileSearch, Layers, Trash2, Copy as CopyIcon, Check, Hash, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, formatBytes, formatDuration, apiBaseUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SearchInput, { filterByName, useUrlSearchState } from "@/components/SearchInput";
import PageHero from "@/components/PageHero";
import RecentMigrationsPulse, { adaptNasMigrations } from "@/components/RecentMigrationsPulse";
import NewDataMigrationWizard from "@/components/NewDataMigrationWizard";

/**
 * NAS-to-NAS migration wizard backed by /api/storage-migrations.
 *
 * State machine surfaces (from backend):
 *   discovering → scanning → awaiting-dedupe → transferring
 *                                            → verifying → completed
 *
 * In preview env the simulator (MOOVBYTE_NAS_SIMULATE=true) advances
 * the job on every poll so the dedupe wizard + live dashboard render
 * with synthetic data. Production wires real C# agent v1.0.23+.
 */
export default function StorageMigrationsNAS() {
    const { jobId } = useParams();
    const navigate = useNavigate();

    if (jobId) {
        return <ActiveMigrationView jobId={jobId} />;
    }
    // The wizard can submit to one of two backends -- storage_migrations.py's
    // same-box copy engine (unencrypted, "Classic Migrations" below) or the
    // real PQC-encrypted agent-to-agent pipeline (POST /jobs, "Gateway
    // Transfers" below). Each has its own detail page and status vocabulary,
    // so onCreated must route to the matching one or the job would be
    // invisible/404 on the wrong page.
    return (
        <LandingView
            onCreated={(id, backend) =>
                navigate(backend === "jobs" ? `/jobs/${id}` : `/migrate/storage/${id}`)
            }
        />
    );
}

// ---- Landing — list of past + new-migration CTA -----------------------------

function LandingView({ onCreated }) {
    const [tab, setTab] = useState("classic"); // "classic" | "gateway"
    const [migrations, setMigrations] = useState([]);
    const [gatewayJobs, setGatewayJobs] = useState([]);
    const [showNew, setShowNew] = useState(false);
    const [search, setSearch] = useUrlSearchState("q");

    const refresh = useCallback(async () => {
        try {
            const { data } = await http.get("/storage-migrations");
            setMigrations(data);
        } catch (e) {
            toast.error(errMsg(e));
        }
        try {
            // Gateway Transfers: real PQC agent-to-agent jobs whose source
            // or destination is a NAS/S3/Azure/GCS connection record --
            // created via /jobs (migration_kind=storage), never visible
            // from the classic-migrations list above since they live in a
            // different collection with a different status vocabulary.
            const { data } = await http.get("/jobs", { params: { migration_kind: "storage" } });
            setGatewayJobs((data || []).filter((j) => j.source_storage_id || j.dest_storage_id));
        } catch (e) {
            toast.error(errMsg(e));
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const visibleMigrations = React.useMemo(
        () => filterByName(migrations, search, ["name", "source_agent_name", "dest_agent_name", "id"]),
        [migrations, search],
    );
    const visibleGatewayJobs = React.useMemo(
        () => filterByName(gatewayJobs, search, ["name", "source_agent_name", "dest_agent_name", "id"]),
        [gatewayJobs, search],
    );

    return (
        <div className="mx-auto max-w-[1400px] space-y-5 p-6">
            <PageHero
                eyebrow="Data Migration Engine"
                title="Data Migration"
                description="Parallel scan + fast-hash dedupe + adaptive multi-worker transfer with checkpoint resume. Move data between NAS boxes (NetApp / PureStorage / Synology / QNAP / TrueNAS), Azure Blob Storage, and direct PC/server storage."
                icon={HardDrive}
                tint="#22c55e"
                testId="nas-page-hero"
                actions={
                    <Button
                        onClick={() => setShowNew(true)}
                        data-testid="nas-new-migration-btn"
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        <Camera size={14} className="mr-2" /> New Data Migration
                    </Button>
                }
            />

            <div className="flex items-center gap-1 border-b border-border" data-testid="nas-tabs">
                <TabButton active={tab === "classic"} onClick={() => setTab("classic")} testid="nas-tab-classic">
                    Classic Migrations
                </TabButton>
                <TabButton active={tab === "gateway"} onClick={() => setTab("gateway")} testid="nas-tab-gateway">
                    Gateway Transfers
                </TabButton>
            </div>

            {tab === "classic" ? (
                migrations.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border bg-surface p-10 text-center text-sm text-muted-foreground">
                        No data migrations yet. Click &quot;New Data Migration&quot; to start one.
                    </div>
                ) : (
                    <>
                        <RecentMigrationsPulse items={adaptNasMigrations(migrations)} testId="nas-recent-pulse" />

                        <div className="flex items-center justify-between gap-3">
                            <SearchInput
                                value={search}
                                onChange={setSearch}
                                placeholder="Search data migrations by name or agent…"
                                testid="nas-search-input"
                                className="w-full max-w-sm"
                            />
                            <div
                                className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
                                data-testid="nas-search-count"
                            >
                                {visibleMigrations.length} of {migrations.length}
                            </div>
                        </div>

                        <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
                            <table className="w-full text-sm" data-testid="nas-migrations-table">
                                <thead className="border-b border-border bg-background/40">
                                    <tr className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                        <th className="px-4 py-2 text-left">Name</th>
                                        <th className="px-4 py-2 text-left">Source → Destination</th>
                                        <th className="px-4 py-2 text-left">Status</th>
                                        <th className="px-4 py-2 text-left">Created</th>
                                        <th className="px-4 py-2 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleMigrations.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="nas-no-search-results">
                                                No data migrations match <span className="font-mono text-foreground">{search}</span>.
                                            </td>
                                        </tr>
                                    ) : visibleMigrations.map((m) => (
                                    <tr key={m.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/30">
                                        <td className="px-4 py-2.5 font-medium">{m.name || m.id.slice(0, 8)}</td>
                                        <td className="px-4 py-2.5 text-xs">
                                            {m.source_agent_name} <ArrowRight size={10} className="inline" /> {m.dest_agent_name}
                                        </td>
                                        <td className="px-4 py-2.5"><StatusPill status={m.status} /></td>
                                        <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                                            {(m.created_at || "").replace("T", " ").slice(0, 19)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            <Link
                                                to={`/migrate/storage/${m.id}`}
                                                data-testid={`nas-open-${m.id}`}
                                                className="text-xs text-blue-400 hover:text-blue-300"
                                            >
                                                Open →
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    </>
                )
            ) : (
                gatewayJobs.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border bg-surface p-10 text-center text-sm text-muted-foreground">
                        No gateway transfers yet — these are real, PQC-encrypted agent-to-agent transfers
                        involving a NAS/S3/Azure/GCS connection. Click &quot;New Data Migration&quot; and pick a
                        connection as either endpoint to start one.
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between gap-3">
                            <SearchInput
                                value={search}
                                onChange={setSearch}
                                placeholder="Search gateway transfers by name or agent…"
                                testid="gateway-search-input"
                                className="w-full max-w-sm"
                            />
                            <div
                                className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
                                data-testid="gateway-search-count"
                            >
                                {visibleGatewayJobs.length} of {gatewayJobs.length}
                            </div>
                        </div>

                        <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
                            <table className="w-full text-sm" data-testid="gateway-jobs-table">
                                <thead className="border-b border-border bg-background/40">
                                    <tr className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                        <th className="px-4 py-2 text-left">Name</th>
                                        <th className="px-4 py-2 text-left">Source → Destination</th>
                                        <th className="px-4 py-2 text-left">Status</th>
                                        <th className="px-4 py-2 text-left">Created</th>
                                        <th className="px-4 py-2 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleGatewayJobs.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">
                                                No gateway transfers match <span className="font-mono text-foreground">{search}</span>.
                                            </td>
                                        </tr>
                                    ) : visibleGatewayJobs.map((j) => (
                                    <tr key={j.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/30">
                                        <td className="px-4 py-2.5 font-medium">{j.name || j.id.slice(0, 8)}</td>
                                        <td className="px-4 py-2.5 text-xs">
                                            {j.source_storage_name || j.source_agent_name}
                                            {" "}<ArrowRight size={10} className="inline" />{" "}
                                            {j.dest_storage_name || j.dest_agent_name}
                                        </td>
                                        <td className="px-4 py-2.5"><StatusPill status={j.status} /></td>
                                        <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                                            {(j.created_at || "").replace("T", " ").slice(0, 19)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            <Link
                                                to={`/jobs/${j.id}`}
                                                data-testid={`gateway-open-${j.id}`}
                                                className="text-xs text-blue-400 hover:text-blue-300"
                                            >
                                                Open →
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    </>
                )
            )}

            {showNew && (
                <NewDataMigrationWizard
                    onClose={() => setShowNew(false)}
                    onCreated={(id, backend) => { setShowNew(false); onCreated(id, backend); }}
                />
            )}
        </div>
    );
}



// ---- Active migration view (phase-aware) -----------------------------------

function ActiveMigrationView({ jobId }) {
    const [job, setJob] = useState(null);
    const [error, setError] = useState("");

    const refresh = useCallback(async () => {
        try {
            const { data } = await http.get(`/storage-migrations/${jobId}`);
            setJob(data);
            setError("");
        } catch (e) {
            setError(errMsg(e));
        }
    }, [jobId]);

    useEffect(() => {
        refresh();
        // Poll every 1.2s — drives the simulator and refreshes live stats.
        const iv = setInterval(refresh, 1200);
        return () => clearInterval(iv);
    }, [refresh]);

    if (error) {
        return (
            <div className="mx-auto max-w-3xl p-6">
                <div className="rounded-lg border border-rose-400/40 bg-rose-500/5 p-6 text-rose-200">
                    <AlertTriangle size={16} className="mb-2 inline" /> {error}
                </div>
            </div>
        );
    }
    if (!job) return <div className="p-10 text-center text-sm text-muted-foreground"><Loader2 className="inline animate-spin" /> Loading…</div>;

    return (
        <div className="mx-auto max-w-[1400px] p-6 pb-12" data-testid="nas-active-view">
            <Header job={job} />

            {(job.status === "discovering" || job.status === "scanning") && <ScanPanel job={job} />}
            {job.status === "awaiting-dedupe" && (
                <DuplicateResolutionWizard job={job} onSubmitted={refresh} />
            )}
            {(job.status === "transferring" || job.status === "paused"
                || job.status === "verifying" || job.status === "completed"
                || job.status === "completed_with_errors" || job.status === "completed_unverified") && (
                <Dashboard job={job} onAction={refresh} />
            )}
        </div>
    );
}

function Header({ job }) {
    return (
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <div>
                <Link to="/migrate/storage" className="text-xs text-muted-foreground hover:text-foreground">
                    ← Data migrations
                </Link>
                <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">
                    {job.name || `Migration ${job.id.slice(0, 8)}`}
                </h1>
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-mono">{job.source_agent_name}</span>
                    <ArrowRight size={11} />
                    <span className="font-mono">{job.dest_agent_name}</span>
                    <span className="ml-2 rounded bg-zinc-800 px-2 py-0.5 font-mono text-[10px] uppercase">
                        {job.source_nas_type}
                    </span>
                    {job.worker_agent_name && (
                        <span
                            className="rounded border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 font-mono text-[10px] text-blue-200"
                            title="Real agent performing the connection + transfer"
                            data-testid="nas-worker-agent-pill"
                        >
                            via {job.worker_agent_name}
                        </span>
                    )}
                </div>
            </div>
            <StatusPill status={job.status} large />
        </div>
    );
}

// ---- Scan progress panel ----------------------------------------------------

function ScanPanel({ job }) {
    const scan = job.phase_progress?.scan || {};
    const total = scan.total_files_estimate || 0;
    const done = scan.files_scanned || 0;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return (
        <div
            data-testid="nas-scan-panel"
            className="rounded-lg border border-border bg-surface p-6"
        >
            <div className="mb-4 flex items-center gap-3">
                <FileSearch size={18} className="text-blue-400 animate-pulse" />
                <div className="font-display text-xl font-semibold">
                    {job.status === "discovering" ? "Discovering source layout…" : "Scanning files in parallel"}
                </div>
            </div>
            <p className="text-sm text-muted-foreground">
                Multi-threaded walker partitions top-level dirs across workers. Fast-hashing
                first-MB + last-MB is used to detect duplicates without reading whole files.
            </p>
            <div className="mt-5 space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Files scanned</span>
                    <span className="font-mono">{done.toLocaleString()} / {total.toLocaleString()}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded bg-zinc-800">
                    <div
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-500"
                        style={{ width: `${pct}%` }}
                        data-testid="nas-scan-progress-bar"
                    />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{formatBytes(scan.bytes_scanned || 0)} read</span>
                    <span>{pct}%</span>
                </div>
            </div>
        </div>
    );
}

// ---- Duplicate resolution wizard -------------------------------------------

function DuplicateResolutionWizard({ job, onSubmitted }) {
    const initial = useMemo(() => {
        const m = {};
        for (const g of job.duplicates || []) {
            m[g.hash] = { action: "keep_oldest", selected: [] };
        }
        return m;
    }, [job.duplicates]);
    const [decisions, setDecisions] = useState(initial);
    const [submitting, setSubmitting] = useState(false);

    const totalSavings = (job.duplicates || []).reduce(
        (a, g) => a + g.size * (g.files.length - 1), 0);

    const setGroupAction = (hash, action, selected = []) => {
        setDecisions((d) => ({ ...d, [hash]: { action, selected } }));
    };

    const applyToAll = (action) => {
        const next = {};
        for (const g of job.duplicates || []) next[g.hash] = { action, selected: [] };
        setDecisions(next);
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                decisions: Object.entries(decisions).map(([hash, d]) => ({
                    hash, action: d.action, selected: d.selected || [],
                })),
            };
            await http.post(`/storage-migrations/${job.id}/dedupe`, payload);
            toast.success("Dedupe decisions applied — transfer starting");
            onSubmitted();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="nas-dedupe-wizard" className="space-y-5">
            <div className="rounded-lg border border-amber-400/30 bg-amber-500/[0.06] p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-100">
                    <AlertTriangle size={14} /> {(job.duplicates || []).length} duplicate groups found
                </div>
                <p className="mt-1 text-xs text-amber-200/80">
                    Total potential savings: {formatBytes(totalSavings)} —
                    pick a resolution per group, or apply one to all.
                </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 rounded border border-border bg-surface p-3">
                <span className="text-xs text-muted-foreground">Apply to all:</span>
                {[
                    { v: "keep_oldest", label: "Keep oldest" },
                    { v: "keep_newest", label: "Keep newest" },
                    { v: "keep_all",    label: "Keep all (no dedupe)" },
                ].map((b) => (
                    <button
                        key={b.v}
                        onClick={() => applyToAll(b.v)}
                        data-testid={`nas-apply-all-${b.v}`}
                        className="rounded border border-border bg-background px-3 py-1 text-xs hover:border-blue-500 hover:text-blue-300"
                    >
                        {b.label}
                    </button>
                ))}
            </div>

            <div className="space-y-3">
                {(job.duplicates || []).map((g) => (
                    <DuplicateGroupCard
                        key={g.hash}
                        group={g}
                        decision={decisions[g.hash]}
                        onChange={(action, selected) => setGroupAction(g.hash, action, selected)}
                    />
                ))}
            </div>

            <div className="sticky bottom-4 z-10 flex justify-end">
                <Button
                    onClick={submit}
                    disabled={submitting}
                    data-testid="nas-dedupe-submit-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 shadow-lg"
                >
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                    Apply decisions and start transfer
                </Button>
            </div>
        </div>
    );
}

function DuplicateGroupCard({ group, decision, onChange }) {
    const action = decision?.action || "keep_oldest";
    const selectedKeep = decision?.selected?.[0] || group.files[0]?.path;
    const wasted = group.size * (group.files.length - 1);

    const filesSorted = useMemo(() => {
        return [...group.files].sort((a, b) => (a.modified || "").localeCompare(b.modified || ""));
    }, [group.files]);

    return (
        <div
            data-testid={`nas-dup-group-${group.hash}`}
            className="rounded-lg border border-border bg-surface p-4"
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="flex items-center gap-2 text-sm font-semibold">
                        <Hash size={12} className="text-muted-foreground" />
                        <code className="text-xs">{group.hash.slice(7, 23)}…</code>
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                        {formatBytes(group.size)} × {group.files.length} copies
                    </div>
                </div>
                <span className="rounded bg-zinc-800/60 px-2 py-0.5 font-mono text-[10px] text-zinc-300">
                    {formatBytes(wasted)} wasted
                </span>
            </div>

            <div className="mt-3 space-y-1.5">
                {filesSorted.map((f, i) => {
                    const isKeep = action === "keep_one"
                        ? selectedKeep === f.path
                        : action === "keep_oldest" ? i === 0
                        : action === "keep_newest" ? i === filesSorted.length - 1
                        : true; // keep_all
                    return (
                        <label
                            key={f.path}
                            className={[
                                "flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-xs",
                                isKeep
                                    ? "border-emerald-500/40 bg-emerald-500/5"
                                    : "border-zinc-800 bg-zinc-950/30 opacity-60",
                            ].join(" ")}
                        >
                            <input
                                type={action === "keep_one" ? "radio" : "checkbox"}
                                name={`dup-${group.hash}`}
                                checked={isKeep}
                                onChange={() => action === "keep_one"
                                    ? onChange("keep_one", [f.path])
                                    : null}
                                disabled={action !== "keep_one"}
                                className="h-3 w-3"
                            />
                            <div className="flex-1">
                                <div className="font-mono text-[11px] text-zinc-200">{f.path}</div>
                                <div className="text-[10px] text-muted-foreground">
                                    Modified {new Date(f.modified).toLocaleDateString()}
                                </div>
                            </div>
                            {isKeep && (
                                <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-300">
                                    KEEP
                                </span>
                            )}
                        </label>
                    );
                })}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
                {[
                    { v: "keep_oldest", label: "Keep oldest" },
                    { v: "keep_newest", label: "Keep newest" },
                    { v: "keep_one",    label: "Keep selected" },
                    { v: "keep_all",    label: "Keep all" },
                ].map((b) => (
                    <button
                        key={b.v}
                        onClick={() => onChange(b.v, b.v === "keep_one" ? [selectedKeep] : [])}
                        data-testid={`nas-dup-${group.hash}-${b.v}`}
                        className={[
                            "rounded border px-2.5 py-1 text-[11px]",
                            action === b.v
                                ? "border-blue-500 bg-blue-500/10 text-blue-200"
                                : "border-border text-muted-foreground hover:border-zinc-500",
                        ].join(" ")}
                    >
                        {b.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// ---- Live transfer dashboard ------------------------------------------------

function Dashboard({ job, onAction }) {
    const t = job.phase_progress?.transfer || {};
    const speed = t.current_speed_bps || 0;
    const total = t.bytes_total || 1;
    const done = t.bytes_done || 0;
    const pct = Math.round((done / total) * 100);
    const filesPct = t.files_total > 0 ? Math.round((t.files_done / t.files_total) * 100) : 0;
    const remaining = Math.max(0, total - done);
    const etaSec = speed > 0 ? remaining / speed : 0;

    const pause = async () => {
        try { await http.post(`/storage-migrations/${job.id}/pause`); onAction(); }
        catch (e) { toast.error(errMsg(e)); }
    };
    const resume = async () => {
        try { await http.post(`/storage-migrations/${job.id}/resume`); onAction(); }
        catch (e) { toast.error(errMsg(e)); }
    };
    const cancel = async () => {
        if (!window.confirm("Cancel this migration? Already-transferred files stay on the destination.")) return;
        try { await http.post(`/storage-migrations/${job.id}/cancel`); onAction(); }
        catch (e) { toast.error(errMsg(e)); }
    };

    const [reportBusy, setReportBusy] = useState(false);
    const downloadReport = async () => {
        setReportBusy(true);
        try {
            // fetch directly (not the axios http client) so we can stream
            // the PDF blob — same pattern as the agent inventory report.
            const base = apiBaseUrl();
            const resp = await fetch(`${base}/api/storage-migrations/${job.id}/report.pdf`, {
                credentials: "include",
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `moovbytes-data-migration-${(job.name || job.id).replace(/\s+/g, "_")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);
        } catch (e) {
            toast.error(`Could not generate report: ${e.message || e}`);
        } finally {
            setReportBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="nas-dashboard">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Metric icon={Zap} label="Speed" value={`${(speed / 1_000_000).toFixed(1)} MB/s`} accent="blue" />
                <Metric
                    icon={Layers}
                    label="Files"
                    value={`${(t.files_done || 0).toLocaleString()} / ${(t.files_total || 0).toLocaleString()}`}
                    sublabel={`${filesPct}%`}
                />
                <Metric
                    icon={Trash2}
                    label="Dedupe savings"
                    value={formatBytes(job.duplicate_savings_bytes || 0)}
                    sublabel={`${(job.duplicates || []).length} groups`}
                    accent="amber"
                />
                <Metric
                    icon={Activity}
                    label="ETA"
                    value={etaSec > 0 ? formatDuration(etaSec) : (job.status.startsWith("completed") ? "done" : "—")}
                    sublabel={job.status}
                />
            </div>

            {/* Master progress bar */}
            <div className="rounded-lg border border-border bg-surface p-5">
                <div className="mb-2 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{formatBytes(done)} of {formatBytes(total)}</span>
                    <span className="font-mono text-zinc-200">{pct}%</span>
                </div>
                <div className="h-3 w-full overflow-hidden rounded bg-zinc-800">
                    <div
                        className="h-full bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 transition-all duration-500"
                        style={{ width: `${pct}%` }}
                        data-testid="nas-transfer-progress-bar"
                    />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    {job.status === "transferring" && (
                        <Button onClick={pause} variant="outline" size="sm" data-testid="nas-pause-btn">
                            <Pause size={12} className="mr-1.5" /> Pause
                        </Button>
                    )}
                    {job.status === "paused" && (
                        <Button onClick={resume} size="sm" data-testid="nas-resume-btn" className="bg-blue-600 hover:bg-blue-700">
                            <Play size={12} className="mr-1.5" /> Resume
                        </Button>
                    )}
                    {!["completed", "completed_with_errors", "completed_unverified", "cancelled", "failed"].includes(job.status) && (
                        <Button onClick={cancel} variant="outline" size="sm" data-testid="nas-cancel-btn"
                            className="border-rose-400/40 text-rose-300 hover:bg-rose-500/10">
                            <X size={12} className="mr-1.5" /> Cancel
                        </Button>
                    )}
                    {t.checkpoint && (
                        <span className="ml-auto rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] text-emerald-300"
                            data-testid="nas-checkpoint-pill"
                        >
                            ✓ checkpoint @ {(t.checkpoint.saved_at || "").slice(11, 19)}
                        </span>
                    )}
                    {(job.options?.bandwidth_cap_mbps || 0) > 0 && (
                        <span
                            data-testid="nas-bandwidth-cap-pill"
                            className="rounded border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 font-mono text-[10px] text-blue-300"
                            title="Bandwidth cap configured on this migration"
                        >
                            ⏬ cap {job.options.bandwidth_cap_mbps} MB/s
                        </span>
                    )}
                </div>
            </div>

            {/* Live worker grid */}
            <div className="rounded-lg border border-border bg-zinc-950/40 p-5 font-mono text-xs">
                <div className="mb-3 flex items-center justify-between">
                    <div className="text-zinc-400">
                        <span className="text-zinc-500">Active workers:</span>{" "}
                        <span className="text-blue-300">{(t.workers || []).filter(w => w.status === "busy").length}</span>
                        <span className="text-zinc-500"> / {(t.workers || []).length}</span>
                    </div>
                    <RefreshCw size={11} className={job.status === "transferring" ? "animate-spin text-emerald-400" : "text-zinc-600"} />
                </div>
                <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 lg:grid-cols-4">
                    {(t.workers || []).length === 0 && (
                        <div className="col-span-full text-center text-zinc-600">
                            No active workers — phase {job.status}.
                        </div>
                    )}
                    {(t.workers || []).map((w) => (
                        <div
                            key={w.id}
                            data-testid={`nas-worker-${w.id}`}
                            className="flex items-center gap-2 rounded border border-zinc-800 bg-zinc-900/40 p-2"
                        >
                            <span
                                className={[
                                    "h-2 w-2 rounded-full",
                                    w.status === "busy" ? "bg-green-500 animate-pulse" : "bg-zinc-600",
                                ].join(" ")}
                            />
                            <div className="min-w-0 flex-1">
                                <div className="text-[10px] text-zinc-500">Worker {w.id}</div>
                                <div className="truncate text-[11px] text-zinc-200">
                                    {w.current_file || "idle"}
                                </div>
                            </div>
                            {w.current_progress != null && (
                                <span className="text-[10px] text-blue-300">{w.current_progress}%</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Conflicts banner */}
            {(job.conflicts || []).length > 0 && (
                <div className="rounded border border-amber-400/40 bg-amber-500/5 p-3" data-testid="nas-conflicts-banner">
                    <div className="flex items-center gap-2 text-sm text-amber-200">
                        <AlertTriangle size={13} /> {(job.conflicts || []).length} conflict(s) detected
                    </div>
                </div>
            )}

            {/* Completion banner — covers a clean finish as well as the two
                "finished but you should look" outcomes, since those are
                exactly when the report matters most. */}
            {["completed", "completed_with_errors", "completed_unverified"].includes(job.status) && (() => {
                const v = job.verification_progress || {};
                const verified = v.verified_count || 0;
                const failed = v.failed_count || 0;
                const clean = job.status === "completed" && failed === 0;
                const cls = clean
                    ? "border-emerald-500/40 bg-emerald-500/[0.07] text-emerald-200"
                    : "border-amber-500/40 bg-amber-500/[0.07] text-amber-200";
                const title = clean ? "Migration complete"
                    : job.status === "completed_unverified" ? "Migration complete — verification timed out"
                    : "Migration complete — issues found";
                return (
                    <div className={`rounded-lg border p-5 ${cls}`} data-testid="nas-completed-banner">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                {clean ? <CheckCheck size={16} /> : <AlertTriangle size={16} />}
                                <span className="font-display text-lg font-semibold">{title}</span>
                            </div>
                            <Button
                                onClick={downloadReport}
                                disabled={reportBusy}
                                size="sm"
                                variant="outline"
                                data-testid="nas-download-report-btn"
                                className={clean ? "border-emerald-500/40 hover:bg-emerald-500/10" : "border-amber-500/40 hover:bg-amber-500/10"}
                            >
                                {reportBusy ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <FileDown size={12} className="mr-1.5" />}
                                Generate Report
                            </Button>
                        </div>
                        <div className="mt-1 text-xs opacity-80">
                            {(t.files_done || 0).toLocaleString()} files ·{" "}
                            {formatBytes(t.bytes_done || 0)} transferred ·
                            saved {formatBytes(job.duplicate_savings_bytes || 0)} via dedupe.
                        </div>
                        {(verified > 0 || failed > 0) && (
                            <div className="mt-2 flex items-center gap-3 text-xs" data-testid="nas-verify-summary">
                                <span className="text-emerald-300">{verified.toLocaleString()} verified OK</span>
                                {failed > 0 && <span className="text-rose-300">{failed.toLocaleString()} failed verification</span>}
                            </div>
                        )}
                    </div>
                );
            })()}
        </div>
    );
}

// ---- Tiny shared bits -------------------------------------------------------

function Metric({ icon: Icon, label, value, sublabel, accent }) {
    const accentCls = accent === "blue" ? "text-blue-300"
        : accent === "amber" ? "text-amber-300"
        : "text-zinc-100";
    return (
        <div className="rounded-lg border border-border bg-surface p-4" data-testid={`nas-metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <Icon size={11} /> {label}
            </div>
            <div className={`mt-1.5 font-display text-xl font-bold ${accentCls}`}>{value}</div>
            {sublabel && <div className="mt-0.5 text-[10px] text-muted-foreground">{sublabel}</div>}
        </div>
    );
}

function TabButton({ active, onClick, testid, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={[
                "-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                active
                    ? "border-blue-500 text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
            ].join(" ")}
        >
            {children}
        </button>
    );
}

function StatusPill({ status, large }) {
    const meta = {
        discovering:      { cls: "border-blue-400/40 text-blue-200 bg-blue-500/10", label: "discovering" },
        scanning:         { cls: "border-blue-400/40 text-blue-200 bg-blue-500/10", label: "scanning" },
        "awaiting-dedupe": { cls: "border-amber-400/40 text-amber-200 bg-amber-500/10", label: "review duplicates" },
        transferring:     { cls: "border-emerald-400/40 text-emerald-200 bg-emerald-500/10", label: "transferring" },
        paused:           { cls: "border-zinc-400/40 text-zinc-200 bg-zinc-500/10", label: "paused" },
        verifying:        { cls: "border-cyan-400/40 text-cyan-200 bg-cyan-500/10", label: "verifying" },
        completed:        { cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", label: "completed" },
        completed_with_errors: { cls: "border-amber-500/40 text-amber-300 bg-amber-500/10", label: "completed — issues found" },
        completed_unverified:  { cls: "border-amber-500/40 text-amber-300 bg-amber-500/10", label: "completed — unverified" },
        cancelled:        { cls: "border-zinc-500/40 text-zinc-400 bg-zinc-700/30", label: "cancelled" },
        failed:           { cls: "border-rose-400/40 text-rose-300 bg-rose-500/10", label: "failed" },
        // /jobs' status vocabulary (Gateway Transfers tab) — a different
        // set of values than storage_migrations.py's above.
        queued:           { cls: "border-blue-400/40 text-blue-200 bg-blue-500/10", label: "queued" },
        running:          { cls: "border-emerald-400/40 text-emerald-200 bg-emerald-500/10", label: "running" },
        succeeded:        { cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", label: "succeeded" },
    }[status] || { cls: "border-border text-muted-foreground", label: status };
    const sizeCls = large ? "px-3 py-1 text-xs" : "px-2 py-0.5 text-[10px]";
    return (
        <span
            data-testid={`nas-status-pill-${status}`}
            className={`inline-flex items-center gap-1 rounded border ${meta.cls} ${sizeCls} font-mono uppercase tracking-wider`}
        >
            {meta.label}
        </span>
    );
}
